import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ArchiveIcon from '@mui/icons-material/Archive';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import axios from "axios";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { Link, useLocation } from "react-router-dom";

const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

const FREE_STORAGE_LIMIT = 500 * 1024 * 1024;       // 500 MB
const PRO_STORAGE_LIMIT  = 10 * 1024 * 1024 * 1024; // 10 GB

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === 'light' ? '#0ea5e9' : '#308fe8', // sky-500
  },
}));

const handleLogout = async () => {
    await axios({
      method: "POST",
      url: `${backEndUrl}/auth/logout`, 
      withCredentials: true,
    }).then((response) => {
      // redirect to /
      window.location.href = '/';
    });
};

const Sidebar = (props) => {
  const location = useLocation();
  const storageLimit = (props.subscription && props.subscription !== 'free') ? PRO_STORAGE_LIMIT : FREE_STORAGE_LIMIT;
  const percentageUsed = (props.storageUsed / storageLimit) * 100;
  const path = location.pathname;

  // Helper to determine if link is active
  const isActive = (p) => path === p || (p !== '/dashboard' && path.startsWith(p));

  const getLinkClasses = (linkPath) => {
    const active = isActive(linkPath);
    return `group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
      active 
        ? 'bg-sky-50   border-sky-100' 
        : 'hover:bg-slate-50  border-transparent'
    }`;
  };

  const getIconContainerClasses = (linkPath) => {
    const active = isActive(linkPath);
    return `w-8 h-8 rounded-lg flex items-center justify-center shadow-sm transition-colors ${
      active
        ? 'bg-sky-500 text-white shadow-md shadow-sky-200'
        : 'bg-white  border-slate-800 text-slate-400 group-hover:border-sky-200 group-hover:text-sky-500'
    }`;
  };

  const getTextClasses = (linkPath) => {
    const active = isActive(linkPath);
    return `text-sm font-semibold ${
      active
        ? 'text-sky-900'
        : 'text-slate-600 group-hover:text-slate-900'
    }`;
  };

  return (
    <aside className="w-72 bg-white h-screen sticky top-0 border-r border-slate-100 flex flex-col justify-between shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-20">
      
      {/* TOP SECTION */}
      <div className="px-6 py-8">
          {/* Logo Area */}
          <div className="flex items-center gap-3 px-2 mb-10">
              <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-200">
                  <DocumentCheckIcon className="h-5 w-5" />
              </div>
              <div>
                  <span className="text-xl font-bold text-slate-800 tracking-tight block leading-none">ImmiSpace</span>
                  <div className="flex items-center gap-2 mt-1">

             {/*        {props.subscription !== 'free' && (
                      <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 uppercase translate-y-[2px]">
                        PRO
                      </span>
                    )} */}
               
                  </div>
              </div>
          </div>

          {/* Main Navigation */}
          <nav className="space-y-1">
              
              {/* Dashboard */}
              <Link to="/dashboard" className={getLinkClasses('/dashboard')}>
                  <div className={getIconContainerClasses('/dashboard')}>
                      <DashboardIcon fontSize="small" />
                  </div>
                  <span className={getTextClasses('/dashboard')}>Dashboard</span>
              </Link>

              {/* Clients */}
              <Link to="/dashboard/clients" className={getLinkClasses('/dashboard/clients')}>
                  <div className={getIconContainerClasses('/dashboard/clients')}>
                      <PeopleAltIcon fontSize="small" />
                  </div>
                  <span className={getTextClasses('/dashboard/clients')}>Clients</span>
                  {/* Badge example - optional */}
                  {/* <span className="ml-auto bg-white text-sky-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-100 shadow-sm">14</span> */}
              </Link>

              {/* Checklists */}
              <Link to="/dashboard/templates" className={getLinkClasses('/dashboard/templates')}>
                  <div className={getIconContainerClasses('/dashboard/templates')}>
                      <FactCheckIcon fontSize="small" />
                  </div>
                  <span className={getTextClasses('/dashboard/templates')}>Checklists</span>
              </Link>

              {/* Divider */}
              <div className="my-4 border-t border-slate-50 mx-2"></div>

              {/* Archives */}
              <Link to="/dashboard/archive" className={getLinkClasses('/dashboard/archive')}>
                  <div className={getIconContainerClasses('/dashboard/archive')}>
                      <ArchiveIcon fontSize="small" />
                  </div>
                  <span className={getTextClasses('/dashboard/archive')}>Archives</span>
              </Link>

              {/* Settings */}
              <Link to="/dashboard/settings" className={getLinkClasses('/dashboard/settings')}>
                  <div className={getIconContainerClasses('/dashboard/settings')}>
                      <SettingsIcon fontSize="small" />
                  </div>
                  <span className={getTextClasses('/dashboard/settings')}>Settings</span>
              </Link>

          </nav>
      </div>

      {/* BOTTOM SECTION */}
      <div className="p-6 bg-gradient-to-b from-white to-slate-50/50 border-t border-slate-100">
          
          {/* Concierge CTA */}
          {/* <Link to="/dashboard/support">
            <button className="w-full mb-10 bg-slate-900 hover:bg-slate-800 text-white p-4 rounded-2xl text-left group transition-all duration-300 shadow-xl shadow-slate-200 relative overflow-hidden">
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-white opacity-10 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="flex items-center justify-between mb-2 relative z-10">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sky-300">
                        <SupportAgentOutlinedIcon fontSize="small" />
                    </div>
                    <span className="bg-sky-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Free</span>
                </div>
                <div className="relative z-10">
                    <div className="text-sm font-bold text-white mb-0.5">Need Help?</div>
                    <div className="text-xs text-slate-400 group-hover:text-slate-200">Contact Support</div>
                </div>
            </button>
          </Link> */}

          {/* Storage Bar */}
          <div className="mb-10 px-2">
            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
              <span>Storage</span>
              <span>{Math.round(percentageUsed) || 0}% of {(props.subscription && props.subscription !== 'free') ? '10 GB' : '500 MB'}</span>
            </div>
            <BorderLinearProgress variant="determinate" value={percentageUsed || 0} />
            {percentageUsed >= 100 && 
              <p className="text-[10px] text-red-500 mt-1">Storage limit reached</p>
            }
          </div>

          {/* User Profile */}
          <div className="flex items-center justify-between px-2 pt-4 group border-t border-slate-50">
              <div className="flex items-center gap-3 min-w-0">
                  <div className="relative shrink-0">
                      <div className="w-10 h-10 rounded-full bg-white border-2 border-white shadow-md flex items-center justify-center text-sky-600 font-bold overflow-hidden bg-sky-100">
                          {/* Initials */}
                          {props.name ? props.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                      <p className="mt-1 translate-y-3.5 text-sm font-bold text-slate-800 truncate group-hover:text-sky-600 transition-colors">
                        {props.name || 'User'}
                      </p>
                      <p className="text-xs text-slate-400 truncate" title={props.email}>
                        {props.email || 'Consultant'}
                      </p>
                  </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <div className="flex items-center gap-1">
                  <button onClick={handleLogout} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Logout">
                      <LogoutIcon fontSize="small" />
                  </button>
                </div>
                {props.subscription !== 'free' && (
                  <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-bold text-indigo-700 ring-1 ring-inset ring-indigo-700/10 uppercase">
                    PRO
                  </span>
                )}
                {props.subscription === 'free' && (
                  <span className="inline-flex items-center rounded-full bg-gray-50 px-1.5 py-0.5 text-[9px] font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10 uppercase">
                    FREE
                  </span>
                )}
              </div>
          </div>
          
      </div>

    </aside>
  );
};

export default Sidebar;